import React, { useState, useEffect, useCallback, useContext } from 'react';
import { BsEye, BsTrash, BsPrinter, BsX, BsCopy, BsCheck2, BsInfoCircle, BsShieldCheck } from 'react-icons/bs';
import { Tag, Calendar, Layers } from 'lucide-react';
import '../../styles/shared-redesign.css';
import './style.css';

// Components
import Table from '../../molecules/tablas/view';
import ButtonAction from '../../atoms/buttonAction/view';
import Chip from '../../atoms/chip/container';
import TooltipC from '../../atoms/tooltip/view';
import Modal from '../../atoms/modal/view';
import LoadingView from '../../pages/loading/loading-view';
import FacetedFilter from '../../molecules/FacetedFilter/view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';

// Context & Utils
import { roleContext } from '../../../context/role/context';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { createPdfBlob, createPdfUrl } from '../../pages/invoice/controller';
import { showInvoicePDF } from '../../../usecases/payments-usecase';
import { getSupportDocuments, cancelSupportDocument } from '../../../usecases/documents-usecase';

const filtersConfig = [
    {
        key: 'status',
        label: 'Estado',
        icon: <Tag size={14} />,
        options: [
            { label: 'Emitida', value: 'success' },
            { label: 'Anulada', value: 'canceled' },
            { label: 'Pendiente', value: 'pending' },
            { label: 'Rechazada / Error', value: 'failed' },
        ],
    },
    {
        key: 'model',
        label: 'Origen',
        icon: <Layers size={14} />,
        options: [
            { label: 'Compras', value: 'buy' },
            { label: 'Gastos', value: 'spend' },
        ],
    },
    { key: 'date_from', label: 'Fecha Desde', icon: <Calendar size={14} />, type: 'date' },
    { key: 'date_to', label: 'Fecha Hasta', icon: <Calendar size={14} />, type: 'date' },
];

const STATUS_LABELS = {
    success: 'Emitida',
    Emitted: 'Emitida',
    canceled: 'Anulada',
    Cancelled: 'Anulada',
    failed: 'Rechazada',
    error: 'Error DIAN',
    pending: 'Pendiente',
};

const getChipType = (status) => {
    switch (status) {
        case 'success':
        case 'Emitted':
            return 'success';
        case 'canceled':
        case 'Cancelled':
            return 'secondary';
        case 'failed':
        case 'error':
            return 'danger';
        case 'pending':
            return 'warning';
        default:
            return 'info';
    }
};

function SupportDocumentsTable({ id = 'support-documents-table' }) {
    const { profile } = useContext(roleContext) || {};

    const [docs, setDocs] = useState({
        data: [],
        page: 1,
        page_size: 10,
        total: 0,
        total_pages: 1,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState([]);

    // Modals & Document selection state
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [isLoadingPdf, setIsLoadingPdf] = useState(false);
    const [copiedKey, setCopiedKey] = useState(null);

    const fetchDocuments = useCallback(
        async (page = 1, limit = 10, currentFilters = activeFilters, search = searchQuery) => {
            setIsLoading(true);
            try {
                const params = {
                    page,
                    limit,
                };
                const statusVal = currentFilters.find((f) => f.key === 'status')?.value;
                const modelVal = currentFilters.find((f) => f.key === 'model')?.value;
                const dateFromVal = currentFilters.find((f) => f.key === 'date_from')?.value;
                const dateToVal = currentFilters.find((f) => f.key === 'date_to')?.value;

                if (statusVal) params.status = statusVal;
                if (modelVal) params.model = modelVal;
                if (dateFromVal) params.date_from = dateFromVal;
                if (dateToVal) params.date_to = dateToVal;
                if (search && search.trim() !== '') params.search = search.trim();

                const response = await getSupportDocuments(params);
                if (response && response.data) {
                    setDocs(response);
                } else if (response && response.items) {
                    setDocs({
                        data: response.items,
                        page: response.page || 1,
                        page_size: response.page_size || limit,
                        total: response.total || 0,
                        total_pages: Math.ceil((response.total || 0) / limit),
                    });
                } else {
                    setDocs({
                        data: [],
                        page: 1,
                        page_size: limit,
                        total: 0,
                        total_pages: 1,
                    });
                }
            } catch (error) {
                console.error('Error fetching support documents:', error);
                dispatchCustomEvent('popup', {
                    message: 'No se pudieron cargar los documentos soporte',
                    type: 'error',
                });
                setDocs({ data: [], page: 1, page_size: limit, total: 0, total_pages: 1 });
            } finally {
                setIsLoading(false);
            }
        },
        [activeFilters, searchQuery]
    );

    useEffect(() => {
        fetchDocuments(1, 10);
    }, [fetchDocuments]);

    const handleFiltersChange = (newFilters) => {
        setActiveFilters(newFilters);
        fetchDocuments(1, docs.page_size, newFilters, searchQuery);
    };

    const handleClearFilters = () => {
        setActiveFilters([]);
        fetchDocuments(1, docs.page_size, [], searchQuery);
    };

    const handleSearch = (query) => {
        setSearchQuery(query);
        fetchDocuments(1, docs.page_size, activeFilters, query);
    };

    const handleCopy = (text, key) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedKey(key);
        dispatchCustomEvent('popup', { message: 'Copiado al portapapeles', type: 'info' });
        setTimeout(() => setCopiedKey(null), 2000);
    };

    const handleClosePdf = useCallback(() => {
        if (pdfUrl) {
            URL.revokeObjectURL(pdfUrl);
            setPdfUrl(null);
        }
        dispatchCustomEvent('modal-close-support-doc-pdf');
    }, [pdfUrl]);

    const handleCloseDetail = useCallback(() => {
        setSelectedDoc(null);
        dispatchCustomEvent('modal-close-support-doc-detail');
    }, []);

    const handlePrintRepresentation = () => {
        const iframe = document.querySelector('iframe[title="pdf-support-doc-viewer"]');
        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } else {
            window.print();
        }
    };

    const handleShowPdf = async (row) => {
        const item = row?._original || row;
        setSelectedDoc(item);
        const cudsCode = item.cuds || item.dian_code;

        setIsLoadingPdf(true);
        try {
            if (cudsCode) {
                try {
                    const response = await showInvoicePDF(cudsCode);
                    const pdfData = response?.data || response;
                    if (pdfData && typeof pdfData === 'string' && pdfData.startsWith('JVBER')) {
                        const blob = createPdfBlob(pdfData);
                        const url = createPdfUrl(blob);
                        setPdfUrl(url);
                    } else {
                        setPdfUrl(null);
                    }
                } catch {
                    setPdfUrl(null);
                }
            } else {
                setPdfUrl(null);
            }
        } finally {
            setIsLoadingPdf(false);
            dispatchCustomEvent('modalsupport-doc-pdf');
        }
    };

    const handleCancelSupportDoc = async (doc) => {
        if (!doc) return;
        setIsLoading(true);
        try {
            const prefix = doc.prefix || (doc.consecutive ? doc.consecutive.split('-')[0] : 'DS');
            const number = doc.number || (doc.consecutive ? doc.consecutive.split('-')[1] : '');
            const res = await cancelSupportDocument(prefix, number);
            dispatchCustomEvent('popup', {
                message: res?.message || 'Nota de ajuste emitida y documento soporte anulado exitosamente',
                type: 'success',
            });
            await fetchDocuments(docs.page, docs.page_size);
        } catch (error) {
            console.error('Error cancelling support document:', error);
            const errMsg = error?.response?.data?.message || error?.message || 'Error al anular documento soporte ante la DIAN';
            dispatchCustomEvent('popup', {
                message: errMsg,
                type: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleShowDetail = (row) => {
        const item = row?._original || row;
        setSelectedDoc(item);
        dispatchCustomEvent('modalsupport-doc-detail');
    };

    const mappedData = (docs.data || []).map((item) => {
        const modelLabel = item.model === 'buy' ? 'Compra' : item.model === 'spend' ? 'Gasto' : item.model || 'Documento';
        const modelChipType = item.model === 'buy' ? 'info' : 'primary';
        const statusLabel = STATUS_LABELS[item.status] || item.status || 'Desconocido';

        return {
            date: item.date ? item.date.split(' ')[0].split('T')[0] : '---',
            status: item.error_message ? (
                <TooltipC text={item.error_message}>
                    <Chip type={getChipType(item.status)}>{statusLabel}</Chip>
                </TooltipC>
            ) : (
                <Chip type={getChipType(item.status)}>{statusLabel}</Chip>
            ),
            tipo: <Chip type={modelChipType}>{modelLabel}</Chip>,
            consecutive: item.consecutive || '---',
            supplier: (
                <div className="support-docs-supplier-col">
                    <span className="support-docs-supplier-name">{item.supplier?.name || '---'}</span>
                    {(item.supplier?.identification || item.supplier?.identification_number) && (
                        <span className="support-docs-supplier-nit">
                            NIT: {item.supplier?.identification || item.supplier?.identification_number}
                        </span>
                    )}
                </div>
            ),
            total: parseFloat(item.total || 0),
            _original: item,
        };
    });

    return (
        <div className="support-docs-container">
            {(isLoading || isLoadingPdf) && <LoadingView />}



            {/* Tabla de Documentos Soporte con FacetedFilter y Buscador integrado */}
            <Table
                id={id}
                onReload={() => fetchDocuments(docs.page, docs.page_size)}
                headersList={['Fecha', 'Estado', 'Tipo', 'Consecutivo', 'Proveedor', 'Total']}
                paginatedData={{ ...docs, data: mappedData }}
                getFunction={(p, l) => fetchDocuments(p, l)}
                allowSearch={true}
                onSearch={handleSearch}
                toolbarActions={
                    <FacetedFilter
                        filters={filtersConfig}
                        activeFilters={activeFilters}
                        onChange={handleFiltersChange}
                        onClear={handleClearFilters}
                    />
                }
                allowPageSizeControl={true}
                formats={{ total: { type: 'money' } }}
                excludeList={['_original']}
            >
                <ButtonAction
                    title={<BsEye />}
                    onClick={handleShowPdf}
                    tooltip="Ver Representación / PDF DIAN"
                />
                <ButtonAction
                    title={<BsInfoCircle />}
                    onClick={handleShowDetail}
                    tooltip="Ver Detalle"
                />
                <ButtonAction
                    title={<BsTrash />}
                    onClick={(row) => dispatchCustomEvent('notification-confirm-cancel-support-doc', row._original || row)}
                    disabled={(row) => {
                        const item = row._original || row;
                        const st = item.state || item.status;
                        if (item.has_credit_note || st === 'canceled' || st === 'Cancelled' || st === 'Deleted' || st === 'Reversed') {
                            return true;
                        }
                        return st !== 'Emitted' && st !== 'success';
                    }}
                    tooltip="Anular Documento Soporte"
                />
            </Table>

            <ConfirmPopUp
                eventKey="cancel-support-doc"
                msg="¿Deseas anular este Documento Soporte generando una Nota de Ajuste ante la DIAN?"
                onAcept={handleCancelSupportDoc}
            />

            {/* Modal de Detalle */}
            <Modal id="support-doc-detail" onClose={handleCloseDetail}>
                {selectedDoc && (
                    <div className="support-docs-detail-modal">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
                                    Documento Soporte {selectedDoc.consecutive}
                                </h3>
                                <Chip type={getChipType(selectedDoc.status)}>
                                    {STATUS_LABELS[selectedDoc.status] || selectedDoc.status}
                                </Chip>
                            </div>

                        </div>

                        <div className="support-docs-detail-grid">
                            <div className="support-docs-detail-field">
                                <span className="support-docs-detail-label">Origen</span>
                                <span className="support-docs-detail-value">
                                    {selectedDoc.model === 'buy' ? 'Factura de Compra' : 'Comprobante de Gasto'} {selectedDoc.model_id ? `#${selectedDoc.model_id}` : ''}
                                </span>
                            </div>
                            <div className="support-docs-detail-field">
                                <span className="support-docs-detail-label">Fecha de Emisión</span>
                                <span className="support-docs-detail-value">{selectedDoc.date || '---'}</span>
                            </div>
                            <div className="support-docs-detail-field">
                                <span className="support-docs-detail-label">Proveedor / Sujeto no obligado</span>
                                <span className="support-docs-detail-value">{selectedDoc.supplier?.name || '---'}</span>
                            </div>
                            <div className="support-docs-detail-field">
                                <span className="support-docs-detail-label">Identificación / NIT</span>
                                <span className="support-docs-detail-value">
                                    {selectedDoc.supplier?.identification || selectedDoc.supplier?.identification_number || '---'}
                                </span>
                            </div>
                            <div className="support-docs-detail-field">
                                <span className="support-docs-detail-label">Valor Total</span>
                                <span className="support-docs-detail-value" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#38bdf8' }}>
                                    ${Number(selectedDoc.total || 0).toLocaleString('es-CO')} COP
                                </span>
                            </div>
                            <div className="support-docs-detail-field">
                                <span className="support-docs-detail-label">Resolución DIAN</span>
                                <span className="support-docs-detail-value">{selectedDoc.resolution || '18764116434372'}</span>
                            </div>
                        </div>

                        {selectedDoc.cuds && (
                            <div className="support-docs-detail-field" style={{ background: 'rgba(30, 41, 59, 0.6)', padding: '14px', borderRadius: '8px', border: '1px solid rgba(148, 163, 184, 0.2)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <span className="support-docs-detail-label">CUDS (Código Único Documento Soporte)</span>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button
                                            type="button"
                                            className="support-docs-copy-btn"
                                            onClick={() => handleCopy(selectedDoc.cuds, 'modal-cuds')}
                                            title="Copiar CUDS"
                                        >
                                            {copiedKey === 'modal-cuds' ? <BsCheck2 size={16} color="#22c55e" /> : <BsCopy size={15} />}
                                        </button>

                                    </div>
                                </div>
                                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem', wordBreak: 'break-all', color: '#93c5fd', lineHeight: 1.4 }}>
                                    {selectedDoc.cuds}
                                </span>
                            </div>
                        )}

                        {selectedDoc.error_message && (
                            <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '12px', borderRadius: '8px', color: '#fca5a5', fontSize: '0.85rem' }}>
                                <strong>Error DIAN:</strong> {selectedDoc.error_message}
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Modal Visor / Representación Gráfica PDF */}
            <Modal id="support-doc-pdf" className="full-viewport-modal" onClose={handleClosePdf}>
                <div className="pdf-viewer-container">
                    <div className="pdf-viewer-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>
                                Documento Soporte Electrónico {selectedDoc?.consecutive || ''}
                            </h3>

                        </div>
                        <div className="pdf-viewer-actions">
                            <button onClick={handlePrintRepresentation} className="pdf-viewer-btn pdf-viewer-btn-secondary">
                                <BsPrinter size={18} />
                                Imprimir
                            </button>
                            <button onClick={handleClosePdf} className="pdf-viewer-btn pdf-viewer-btn-danger">
                                <BsX size={22} />
                                Cerrar
                            </button>
                        </div>
                    </div>

                    <div className="pdf-viewer-iframe-wrapper">
                        {pdfUrl ? (
                            <iframe
                                src={pdfUrl}
                                className="pdf-viewer-iframe"
                                title="pdf-support-doc-viewer"
                            />
                        ) : selectedDoc ? (
                            <div className="support-doc-printable-voucher" id="support-doc-printable" style={{ color: '#0f172a', background: '#ffffff' }}>
                                <div className="voucher-header">
                                    <div className="voucher-company-info">
                                        <h2 className="voucher-company-title" style={{ color: '#0f172a', fontWeight: 900 }}>
                                            {profile?.companyName || profile?.name || 'EMISOR'}
                                        </h2>
                                        <p className="voucher-subtext" style={{ color: '#334155' }}>NIT: {profile?.document || '---'}</p>
                                        <p className="voucher-subtext" style={{ color: '#334155' }}>{profile?.address || ''}</p>
                                        <p className="voucher-subtext" style={{ color: '#334155' }}>{profile?.phone || ''}</p>
                                    </div>
                                    <div className="voucher-doc-box">
                                        <span className="voucher-doc-type" style={{ color: '#1d4ed8', fontWeight: 800 }}>DOCUMENTO SOPORTE EN ADQUISICIONES EFECTUADAS A NO OBLIGADOS A FACTURAR</span>
                                        <h3 className="voucher-doc-consecutive" style={{ color: '#0f172a', fontWeight: 900 }}>{selectedDoc.consecutive}</h3>
                                        <p className="voucher-subtext" style={{ color: '#334155' }}>Resolución DIAN: {selectedDoc.resolution || '18764116434372'}</p>
                                        <p className="voucher-subtext" style={{ color: '#334155' }}>Fecha: {selectedDoc.date || '---'}</p>
                                        <span className="voucher-dian-badge" style={{ color: '#166534', background: '#dcfce7', borderColor: '#86efac' }}>
                                            <BsShieldCheck size={14} color="#166534" /> Documento Validado por la DIAN
                                        </span>
                                    </div>
                                </div>

                                <div className="voucher-section-grid">
                                    <div className="voucher-card">
                                        <h4 style={{ color: '#1e293b', fontWeight: 800 }}>DATOS DEL ADQUIRIENTE (COMPRADOR)</h4>
                                        <p style={{ color: '#334155' }}><strong style={{ color: '#0f172a' }}>Nombre / Razón Social:</strong> {profile?.companyName || profile?.name || '---'}</p>
                                        <p style={{ color: '#334155' }}><strong style={{ color: '#0f172a' }}>NIT:</strong> {profile?.document || '---'}</p>
                                        <p style={{ color: '#334155' }}><strong style={{ color: '#0f172a' }}>Email:</strong> {profile?.email || '---'}</p>
                                    </div>
                                    <div className="voucher-card">
                                        <h4 style={{ color: '#1e293b', fontWeight: 800 }}>DATOS DEL PROVEEDOR (PRESTADOR)</h4>
                                        <p style={{ color: '#334155' }}><strong style={{ color: '#0f172a' }}>Nombre / Razón Social:</strong> {selectedDoc.supplier?.name || '---'}</p>
                                        <p style={{ color: '#334155' }}><strong style={{ color: '#0f172a' }}>Identificación / NIT:</strong> {selectedDoc.supplier?.identification || selectedDoc.supplier?.identification_number || '---'}</p>
                                        <p style={{ color: '#334155' }}><strong style={{ color: '#0f172a' }}>Tipo Sujeto:</strong> No obligado a expedir factura</p>
                                    </div>
                                </div>

                                <div className="voucher-items-section">
                                    <table className="voucher-table">
                                        <thead>
                                            <tr>
                                                <th style={{ color: '#0f172a' }}>Descripción</th>
                                                <th style={{ textAlign: 'center', color: '#0f172a' }}>Cant.</th>
                                                <th style={{ textAlign: 'right', color: '#0f172a' }}>Valor Unitario</th>
                                                <th style={{ textAlign: 'right', color: '#0f172a' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={{ color: '#0f172a', fontWeight: 500 }}>
                                                    {selectedDoc.model === 'buy' ? 'Adquisición de bienes según compra' : 'Honorarios / Prestación de servicios según gasto'}
                                                    {selectedDoc.model_id ? ` #${selectedDoc.model_id}` : ''}
                                                </td>
                                                <td style={{ textAlign: 'center', color: '#0f172a', fontWeight: 600 }}>1</td>
                                                <td style={{ textAlign: 'right', color: '#0f172a', fontWeight: 600 }}>${Number(selectedDoc.total || 0).toLocaleString('es-CO')} COP</td>
                                                <td style={{ textAlign: 'right', color: '#0f172a', fontWeight: 700 }}>${Number(selectedDoc.total || 0).toLocaleString('es-CO')} COP</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div className="voucher-totals-wrapper">
                                    <div className="voucher-totals-box">
                                        <div className="voucher-total-line">
                                            <span style={{ color: '#334155' }}>Subtotal:</span>
                                            <span style={{ color: '#0f172a' }}>${Number(selectedDoc.total || 0).toLocaleString('es-CO')} COP</span>
                                        </div>
                                        <div className="voucher-total-line">
                                            <span style={{ color: '#334155' }}>IVA (0%):</span>
                                            <span style={{ color: '#0f172a' }}>$0 COP</span>
                                        </div>
                                        <div className="voucher-total-line voucher-total-line-final" style={{ borderTop: '2px solid #0f172a', paddingTop: '8px', fontSize: '1.15rem', fontWeight: 800 }}>
                                            <span style={{ color: '#0f172a' }}>TOTAL A PAGAR:</span>
                                            <span style={{ color: '#0f172a' }}>${Number(selectedDoc.total || 0).toLocaleString('es-CO')} COP</span>
                                        </div>
                                    </div>
                                </div>

                                {selectedDoc.cuds && (
                                    <div className="voucher-cuds-footer">
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <strong style={{ color: '#0f172a' }}>CUDS (Código Único de Documento Soporte):</strong>
                                        </div>
                                        <p style={{ wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '0.8rem', margin: 0, color: '#1e293b', fontWeight: 600 }}>
                                            {selectedDoc.cuds}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>
                </div>
            </Modal>
        </div>
    );
}

export default SupportDocumentsTable;
